import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

// ---------------------------------------------------------------------------
// Questionnaires de satisfaction et d'insertion (CDC §8.2). Envoyés d'office
// par le passage quotidien ; remplis dans l'espace apprenant ou par le lien
// personnel du mail ; composés et lus dans l'écran « Satisfaction ».
// ---------------------------------------------------------------------------

export type TypeQuestionnaire =
  | 'SATISFACTION_2M'
  | 'SATISFACTION_6M'
  | 'SATISFACTION_FIN'
  | 'INSERTION_3M'
  | 'INSERTION_6M';

export type TypeQuestion = 'NOTE' | 'NPS' | 'CHOIX_UNIQUE' | 'CHOIX_MULTIPLE' | 'TEXTE';

export const LIBELLES_TYPE_QUESTION: Record<TypeQuestion, string> = {
  NOTE: 'Note de 1 à 5',
  NPS: 'Recommandation de 0 à 10 (NPS)',
  CHOIX_UNIQUE: 'Choix unique',
  CHOIX_MULTIPLE: 'Choix multiple',
  TEXTE: 'Texte libre',
};

export interface QuestionQuestionnaire {
  id?: string;
  libelle: string;
  type: TypeQuestion;
  choix: { id?: string; libelle: string }[];
}

/** Question telle qu'elle a été envoyée : identifiants posés par l'API. */
export interface QuestionPosee {
  id: string;
  libelle: string;
  type: TypeQuestion;
  choix: { id: string; libelle: string }[];
}

export type ReponsesQuestionnaire = Record<string, { note?: number; choix?: string[]; texte?: string }>;

export interface QuestionnaireARemplir {
  id: string;
  type: TypeQuestionnaire;
  titre: string;
  introduction: string | null;
  prenom: string;
  questions: QuestionPosee[];
  envoyeLe: string;
  repondLe: string | null;
}

export interface ModeleQuestionnaire {
  type: TypeQuestionnaire;
  libelleType: string;
  titre: string;
  introduction: string;
  questions: QuestionQuestionnaire[];
  enregistre: boolean;
  modifieLe: string | null;
  modifiePar: string | null;
}

export interface ReponseModeles {
  modeles: ModeleQuestionnaire[];
  peutComposer: boolean;
}

/**
 * Net Promoter Score (TF-434) : promoteurs 9-10, passifs 7-8, détracteurs 0-6 ;
 * score = % promoteurs − % détracteurs, de −100 à +100.
 */
export interface Nps {
  repondants: number;
  promoteurs: number;
  passifs: number;
  detracteurs: number;
  parts: { promoteurs: number; passifs: number; detracteurs: number } | null;
  score: number | null;
}

export type CategorieNps = 'PROMOTEUR' | 'PASSIF' | 'DETRACTEUR';

export interface SyntheseQuestion {
  id: string;
  libelle: string;
  type: TypeQuestion;
  repondants: number;
  moyenne: number | null;
  /** Pour la recommandation de 0 à 10 seulement. */
  nps: Nps | null;
  repartition: { libelle: string; nombre: number }[];
  textes: string[];
}

export interface ResultatsQuestionnaire {
  type: TypeQuestionnaire;
  libelleType: string;
  envoyes: number;
  repondus: number;
  tauxReponse: number | null;
  synthese: SyntheseQuestion[];
  detail:
    | { apprenant: string; siteCode: string; formation: string | null; repondLe: string; reponses: ReponsesQuestionnaire }[]
    | null;
  questions: QuestionPosee[];
}

function invalider(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['questionnaires'] });
}

// --- Espace apprenant ------------------------------------------------------

export function useMesQuestionnaires() {
  return useQuery({
    queryKey: ['questionnaires', 'miens'],
    queryFn: () => appelApi<QuestionnaireARemplir[]>('/espace-apprenant/questionnaires'),
  });
}

export function useRepondreQuestionnaire() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: string; reponses: ReponsesQuestionnaire }) =>
      appelApi<{ message: string }>(`/espace-apprenant/questionnaires/${variables.id}/reponses`, {
        methode: 'POST',
        corps: { reponses: variables.reponses },
      }),
    onSuccess: () => invalider(client),
  });
}

// --- Lien personnel, sans session ---------------------------------------------

export function useQuestionnaireParLien(jeton: string) {
  return useQuery({
    queryKey: ['questionnaires', 'lien', jeton],
    queryFn: () => appelApi<QuestionnaireARemplir>(`/questionnaires/lien/${encodeURIComponent(jeton)}`),
    retry: false,
  });
}

export function useRepondreParLien(jeton: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (reponses: ReponsesQuestionnaire) =>
      appelApi<{ message: string }>(`/questionnaires/lien/${encodeURIComponent(jeton)}/reponses`, {
        methode: 'POST',
        corps: { reponses },
      }),
    onSuccess: () => invalider(client),
  });
}

// --- Écran « Satisfaction » ---------------------------------------------------

export function useModelesQuestionnaires() {
  return useQuery({
    queryKey: ['questionnaires', 'modeles'],
    queryFn: () => appelApi<ReponseModeles>('/questionnaires/modeles'),
  });
}

export function useEnregistrerModeleQuestionnaire() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ type, ...corps }: { type: TypeQuestionnaire; titre: string; introduction?: string; questions: QuestionQuestionnaire[] }) =>
      appelApi<ReponseModeles>(`/questionnaires/modeles/${type}`, { methode: 'PUT', corps }),
    onSuccess: (reponse) => client.setQueryData(['questionnaires', 'modeles'], reponse),
  });
}

export function useResultatsQuestionnaires(site: string, formation: string, actif = true) {
  const parametres = new URLSearchParams();
  if (site && site !== 'TOUS') parametres.set('site', site);
  if (formation) parametres.set('formation', formation);
  const requete = parametres.toString();
  return useQuery({
    queryKey: ['questionnaires', 'resultats', requete],
    queryFn: () =>
      appelApi<ResultatsQuestionnaire[]>(`/questionnaires/resultats${requete ? `?${requete}` : ''}`),
    enabled: actif,
  });
}

// --- Tableau de bord de satisfaction (TF-433) ----------------------------------
// À chaud (satisfaction à 2 mois, 6 mois, fin de parcours) et à froid (insertion
// à 3 et 6 mois après l'examen), par formation, formateur référent et promotion.
// L'API compte tout ; l'écran ne fait qu'afficher et paginer les commentaires.

export type FamilleQuestionnaire = 'CHAUD' | 'FROID';
export type AxeSatisfaction = 'FORMATION' | 'FORMATEUR' | 'PROMOTION';
export type ChoixPeriodeSatisfaction = 'DOUZE_MOIS' | 'ANNEE' | 'ANNEE_PRECEDENTE' | 'LIBRE';

export interface GroupeSatisfaction {
  cle: string;
  libelle: string;
  siteCode: string | null;
}

export interface PartSatisfaction {
  repondants: number;
  oui: number;
  part: number | null;
}

export interface RetourQuestionnaires {
  envoyes: number;
  repondus: number;
  tauxReponse: number | null;
}

export interface IndicateursChaud {
  reponses: number;
  moyenne: number | null;
  notes: number;
  /** Ancienne question Oui/Non, que gardent les questionnaires envoyés avant le NPS. */
  recommandation: PartSatisfaction;
  nps: Nps;
}

export interface IndicateursFroid {
  reponses: number;
  enEmploi: PartSatisfaction;
  emploiEnLien: PartSatisfaction;
  diplomeObtenu: PartSatisfaction;
  gardeParEntreprise: PartSatisfaction;
}

export interface CritereSatisfaction {
  id: string;
  libelle: string;
  moyenne: number | null;
  notes: number;
  questionnaires: TypeQuestionnaire[];
  moyennePrecedente: number | null;
  ecart: number | null;
}

export interface PointEvolution {
  mois: string;
  libelle: string;
  chaud: { reponses: number; moyenne: number | null; recommandation: number | null; nps: number | null };
  froid: { reponses: number; enEmploi: number | null };
}

export interface LigneGroupeSatisfaction extends GroupeSatisfaction {
  chaud: IndicateursChaud & RetourQuestionnaires & { moyennePrecedente: number | null; ecart: number | null };
  froid: IndicateursFroid & RetourQuestionnaires;
}

export interface CommentaireSatisfaction {
  id: string;
  repondLe: string;
  type: TypeQuestionnaire;
  libelleType: string;
  famille: FamilleQuestionnaire;
  question: string;
  texte: string;
  moyenne: number | null;
  recommande: boolean | null;
  /** Recommandation de 0 à 10 du même questionnaire. */
  nps: number | null;
  situation: string | null;
  apprenant: string | null;
  siteCode: string;
  formation: string;
  formateur: string;
  promotion: string;
}

export interface TableauSatisfaction {
  periode: {
    choix: ChoixPeriodeSatisfaction;
    du: string;
    au: string;
    arreteeAu: string;
    libelle: string;
    mois: string[];
    comparaison: { du: string; au: string; libelle: string; annees: number };
  };
  lecture: { nominatif: boolean; perimetre: 'SITES' | 'REFERENT'; message: string | null };
  /** Notes basses à traiter (TF-434), pour qui lit les réponses nommées. */
  alertes: AlertesQualite | null;
  familles: FamilleQuestionnaire[];
  chaud: IndicateursChaud &
    RetourQuestionnaires & {
      precedent: IndicateursChaud;
      ecartMoyenne: number | null;
      ecartRecommandation: number | null;
      ecartNps: number | null;
      criteres: CritereSatisfaction[];
    };
  froid: IndicateursFroid &
    RetourQuestionnaires & {
      precedent: IndicateursFroid;
      ecarts: {
        enEmploi: number | null;
        emploiEnLien: number | null;
        diplomeObtenu: number | null;
        gardeParEntreprise: number | null;
      };
    };
  evolution: PointEvolution[];
  groupes: Record<AxeSatisfaction, LigneGroupeSatisfaction[]>;
  commentaires: CommentaireSatisfaction[];
  options: {
    periodes: { valeur: ChoixPeriodeSatisfaction; libelle: string }[];
    axes: { valeur: AxeSatisfaction; libelle: string }[];
    questionnaires: { valeur: TypeQuestionnaire; libelle: string; famille: FamilleQuestionnaire; libelleFamille: string }[];
    formations: GroupeSatisfaction[];
    formateurs: GroupeSatisfaction[];
    promotions: GroupeSatisfaction[];
  };
  calculeLe: string;
}

export interface FiltresTableauSatisfaction {
  periode: ChoixPeriodeSatisfaction;
  du: string;
  au: string;
  formation: string;
  formateur: string;
  promotion: string;
  questionnaire: string;
}

export const FILTRES_SATISFACTION_PAR_DEFAUT: FiltresTableauSatisfaction = {
  periode: 'DOUZE_MOIS',
  du: '',
  au: '',
  formation: '',
  formateur: '',
  promotion: '',
  questionnaire: '',
};

export function useTableauSatisfaction(site: string, filtres: FiltresTableauSatisfaction) {
  const parametres = new URLSearchParams();
  if (site && site !== 'TOUS') parametres.set('site', site);
  parametres.set('periode', filtres.periode);
  if (filtres.periode === 'LIBRE') {
    if (filtres.du) parametres.set('du', filtres.du);
    if (filtres.au) parametres.set('au', filtres.au);
  }
  for (const cle of ['formation', 'formateur', 'promotion', 'questionnaire'] as const) {
    if (filtres[cle]) parametres.set(cle, filtres[cle]);
  }
  const requete = parametres.toString();
  return useQuery({
    queryKey: ['questionnaires', 'tableau-de-bord', requete],
    queryFn: () => appelApi<TableauSatisfaction>(`/questionnaires/tableau-de-bord?${requete}`),
    // Des dates libres incomplètes ne partent pas : l'API les refuserait.
    enabled: filtres.periode !== 'LIBRE' || Boolean(filtres.du && filtres.au),
    placeholderData: (precedent) => precedent,
    // « Temps réel » (TF-434) : rien n'est mis en cache côté API, l'écran se relit.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

// --- Alertes de note basse et réponse seule (TF-434) ---------------------------

export interface AlerteQualite {
  id: string;
  envoiId: string | null;
  declencheeLe: string;
  apprenant: string | null;
  apprenantId: string | null;
  siteCode: string | null;
  formation: string | null;
  questionnaire: string | null;
  repondLe: string | null;
  notes: { libelle: string; note: number }[];
  recommandation: number | null;
  message: string;
}

export interface AlertesQualite {
  ouvertes: number;
  lignes: AlerteQualite[];
  peutStatuer: boolean;
  /** Une note sur 5 strictement inférieure est basse : réglage de la direction (TF-435). */
  seuil: number;
}

export interface QuestionRepondue {
  id: string;
  libelle: string;
  type: TypeQuestion;
  note: number | null;
  choix: string[];
  texte: string | null;
  basse: boolean;
  categorie: CategorieNps | null;
  libelleCategorie: string | null;
}

export interface ReponseQuestionnaire {
  id: string;
  type: TypeQuestionnaire;
  libelleType: string;
  titre: string;
  envoyeLe: string;
  repondLe: string | null;
  apprenant: {
    id: string;
    nom: string;
    siteCode: string;
    formation: string | null;
    formateur: string | null;
    promotion: string;
  };
  questions: QuestionRepondue[];
  notesBasses: number;
  /** Une note sur 5 strictement inférieure est basse : réglage de la direction (TF-435). */
  seuilNoteBasse: number;
  recommandation: { note: number; categorie: CategorieNps; libelle: string } | null;
  alerte: { id: string; statut: 'OUVERTE' | 'TRAITEE' | 'IGNOREE'; declencheeLe: string; traiteeLe: string | null } | null;
  peutStatuer: boolean;
}

/** Un questionnaire répondu, en entier : la cible de « Voir la réponse ». Chaque lecture est tracée. */
export function useReponseQuestionnaire(id: string | null) {
  return useQuery({
    queryKey: ['questionnaires', 'reponse', id],
    queryFn: () => appelApi<ReponseQuestionnaire>(`/questionnaires/reponses/${encodeURIComponent(id ?? '')}`),
    enabled: Boolean(id),
    retry: false,
  });
}

/** Marquer traitée une alerte de note basse : la route du module d'alertes. */
export function useTraiterAlerteQualite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      appelApi<{ message: string }>(`/alertes/${encodeURIComponent(id)}`, {
        methode: 'PATCH',
        corps: { statut: 'TRAITEE' },
      }),
    onSuccess: () => {
      invalider(client);
      void client.invalidateQueries({ queryKey: ['alertes'] });
    },
  });
}

/** « +62 », « −8 », « 0 », « — » sans répondant. */
export function texteNps(score: number | null): string {
  if (score === null) return '—';
  if (score > 0) return `+${score}`;
  if (score < 0) return `−${Math.abs(score)}`;
  return '0';
}
